import { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_ROW_HEIGHT } from "../constants";

/**
 * useRowWindow — vertical virtualization for the grid body.
 *
 * The grid used to render EVERY row of the (padded) sheet into the DOM
 * on mount.  For large workbooks that is tens of thousands of <tr>s and
 * hundreds of thousands of <td>s materialised up front — the single
 * biggest cause of "the data takes a long time to render".  React.memo
 * on the cells only helps *re-renders*; it does nothing for that initial
 * mount cost.
 *
 * This hook returns the slice of rows that actually need to exist in the
 * DOM for the current scroll position.  GridBody renders that slice plus
 * two spacer rows (above and below) whose heights preserve the real
 * scroll height, so the scrollbar, freeze panes, chart/image overlays
 * and pixel-based scroll-to-cell logic all keep working unchanged.
 *
 * Design notes / invariants:
 *   - Row offsets are visibility-aware: filtered / hidden rows render
 *     `null` (0px), exactly matching how GridBody skips them, so the
 *     spacer math lines up with the real layout.
 *   - Frozen rows ([0, frozenRows)) are ALWAYS rendered by GridBody
 *     outside the window, so the window never starts before frozenRows.
 *   - A merged region must never be split by the window edge (the master
 *     cell carries the rowSpan; its slaves render null).  The window is
 *     expanded to fully contain any merged region it intersects.
 *   - During printing we expand to the full range so the DOM-clone print
 *     path captures every row.
 */

// Pixels of off-screen buffer kept above and below the viewport.  Big
// enough that normal wheel / drag scrolling never reveals a blank gap
// before the next window is computed on the following animation frame.
const OVERSCAN_PX = 800;

export default function useRowWindow({
  gridWrapperRef,
  rowCount,
  rowHeights,
  isRowVisible,
  frozenRows,
  cellMerges,
  activeSheet,
}) {
  // Cumulative top offset of every row (visibility-aware).
  // rowTop[i] = sum of visible row heights for rows [0, i).
  const rowTop = useMemo(() => {
    const arr = new Float64Array(rowCount + 1);
    let acc = 0;
    for (let i = 0; i < rowCount; i += 1) {
      arr[i] = acc;
      if (isRowVisible(i)) acc += rowHeights[i] || DEFAULT_ROW_HEIGHT;
    }
    arr[rowCount] = acc;
    return arr;
  }, [rowCount, rowHeights, isRowVisible]);

  const totalHeight = rowTop[rowCount] || 0;

  // Multi-row merged regions as [startRow, endRowExclusive) pairs.
  const rowSpans = useMemo(() => {
    const spans = [];
    for (const key in cellMerges) {
      const info = cellMerges[key];
      if (!info || !(info.rowSpan > 1)) continue;
      const sep = key.indexOf(":");
      const r = parseInt(key.slice(0, sep), 10);
      spans.push([r, r + info.rowSpan]);
    }
    return spans;
  }, [cellMerges]);

  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  const [printing, setPrinting] = useState(false);

  // Track scroll position + viewport height of the scroll container.
  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el) return undefined;
    let raf = 0;
    const read = () => {
      raf = 0;
      setScrollTop(el.scrollTop);
      setViewportH(el.clientHeight);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(read);
    };
    read();
    el.addEventListener("scroll", onScroll, { passive: true });
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(read);
      ro.observe(el);
    }
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (ro) ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
    // Re-bind when the sheet changes so we re-read the (reset) scroll pos.
  }, [gridWrapperRef, activeSheet]);

  // Render the whole sheet while a print dialog is open (the DOM-clone
  // print path needs every row present).
  useEffect(() => {
    const before = () => setPrinting(true);
    const after = () => setPrinting(false);
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, []);

  // First row whose bottom edge is strictly greater than y.
  const findRow = useCallback((y) => {
    let lo = 0;
    let hi = rowCount;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (rowTop[mid + 1] <= y) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }, [rowTop, rowCount]);

  // Compute the slice.  This runs on every scroll frame (cheap: two
  // binary searches), but the result is reduced to five primitives.
  let startRow;
  let endRow;
  if (rowCount === 0) {
    startRow = 0;
    endRow = 0;
  } else if (printing) {
    startRow = frozenRows;
    endRow = rowCount;
  } else {
    const vh = viewportH || 800;
    let start = findRow(Math.max(0, scrollTop - OVERSCAN_PX));
    let end = findRow(scrollTop + vh + OVERSCAN_PX) + 1;

    if (start < frozenRows) start = frozenRows;
    if (end > rowCount) end = rowCount;

    // Never split a merged region across the window boundary.
    for (let k = 0; k < rowSpans.length; k += 1) {
      const s = rowSpans[k][0];
      const e = rowSpans[k][1];
      if (s < end && e > start) {
        if (s < start) start = Math.max(frozenRows, s);
        if (e > end) end = e;
      }
    }

    if (start < frozenRows) start = frozenRows;
    if (end > rowCount) end = rowCount;
    if (start > end) start = end;

    startRow = start;
    endRow = end;
  }

  const topPad = rowCount === 0 ? 0 : rowTop[startRow] - rowTop[frozenRows];
  const bottomPad = rowCount === 0 ? 0 : totalHeight - rowTop[endRow];

  // Stabilize object identity so GridBody (React.memo) only re-renders
  // when the slice or padding actually changes — not on every scroll px.
  return useMemo(
    () => ({ startRow, endRow, topPad, bottomPad, totalHeight }),
    [startRow, endRow, topPad, bottomPad, totalHeight],
  );
}
